import React, { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowDown, ArrowUp, ChevronDown, Filter, Flag, MonitorSmartphone, TrendingUp, UserSquare2 } from "lucide-react";
import { cn } from "../../../utils/function";

// Automate > Overview (/automate/overview). No backend yet, so every number
// here is generated sample data, seeded per period so it stays stable
// between renders and changes when the period does.

type Period = 7 | 30 | 90;
const PERIODS: { value: Period; label: string }[] = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const CURRENCY = "₦";
const CHANNELS = ["WhatsApp", "SMS", "E-mail", "Push notifications"];
const WORKFLOWS = ["Loan application abandoned", "New account detected", "Dormant reactivation", "Data balance zero"];

type MetricId = "open" | "click" | "conversion" | "abandonment";
const METRICS: { id: MetricId; label: string; icon: React.ElementType; description: string; countLabel: string }[] = [
  { id: "open", label: "Open rate", icon: MonitorSmartphone, description: "Percentage of delivered messages opened", countLabel: "opened" },
  { id: "click", label: "Click-through rate", icon: Flag, description: "Percentage of recipients who clicked", countLabel: "clicked" },
  { id: "conversion", label: "Conversion rate", icon: UserSquare2, description: "How effectively clicks lead to conversions", countLabel: "converted" },
  { id: "abandonment", label: "Abandonment rate", icon: TrendingUp, description: "How many users opted out", countLabel: "opted out" },
];

// Small deterministic PRNG so sample data doesn't jump around on re-render.
const seeded = (seed: number) => () => {
  seed = (seed * 9301 + 49297) % 233280;
  return seed / 233280;
};

interface Row {
  label: string;
  share: number; // 0–100, drives the bar
  value: number;
}

const buildRows = (labels: string[], rand: () => number, scale: number, weights = [1, 0.32, 0.18, 0.12]): Row[] => {
  const raw = labels.map((label, i) => ({ label, value: Math.round(scale * weights[i] * (0.8 + rand() * 0.4)) }));
  const total = raw.reduce((s, r) => s + r.value, 0) || 1;
  return raw.map((r) => ({ ...r, share: Math.round((r.value / total) * 1000) / 10 }));
};

const buildData = (days: Period) => {
  const rand = seeded(days * 7919);
  const today = new Date();
  const series = Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (days - 1 - i));
    const trend = 120 + (i / days) * 220;
    return {
      date: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      revenue: Math.max(20, Math.round(trend + (rand() - 0.5) * 220)),
    };
  });
  const revenueTotal = series.reduce((s, p) => s + p.revenue, 0) * 95;
  const rates: Record<MetricId, number> = {
    open: 38 + rand() * 20,
    click: 9 + rand() * 8,
    conversion: 3 + rand() * 5,
    abandonment: 1 + rand() * 3,
  };
  const delivered = Math.round(days * 410 * (0.9 + rand() * 0.2));
  return {
    series,
    revenueTotal,
    revenueDelta: Math.round((rand() - 0.25) * 16 * 10) / 10,
    activeWorkflows: Math.round(40 + days * 0.95),
    workflowDelta: Math.round(rand() * 6),
    revenueByChannel: buildRows(CHANNELS, rand, revenueTotal),
    revenueByWorkflow: buildRows(WORKFLOWS, rand, revenueTotal, [1, 0.45, 0.25, 0.2]),
    rates,
    byChannel: Object.fromEntries(
      METRICS.map((m) => [m.id, buildRows(CHANNELS, rand, (delivered * rates[m.id]) / 100)])
    ) as Record<MetricId, Row[]>,
    byWorkflow: Object.fromEntries(
      METRICS.map((m) => [m.id, buildRows(WORKFLOWS, rand, (delivered * rates[m.id]) / 100, [1, 0.45, 0.25, 0.2])])
    ) as Record<MetricId, Row[]>,
  };
};

const money = (n: number, compact = false) =>
  `${CURRENCY}${n.toLocaleString("en-NG", compact ? { notation: "compact", maximumFractionDigits: 1 } : { maximumFractionDigits: 0 })}`;

const Delta: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "" }) => {
  const up = value >= 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <p className="mt-3 flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
      <Icon size={14} className={up ? "text-emerald-600" : "text-rose-600"} />
      <span className="font-medium text-gray-900 dark:text-gray-100">
        {up ? "+" : ""}
        {value}
        {suffix}
      </span>
      vs previous period
    </p>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; delta: React.ReactNode }> = ({ icon, label, value, delta }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
    <p className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
      {icon}
      {label}
    </p>
    <p className="mt-3 text-2xl sm:text-[28px] font-medium text-indigo-700 dark:text-indigo-400 underline underline-offset-4 decoration-1">{value}</p>
    {delta}
  </div>
);

const BarList: React.FC<{ title: string; rows: Row[]; format: (n: number) => string }> = ({ title, rows, format }) => {
  const max = Math.max(...rows.map((r) => r.share), 1);
  return (
    <div className="min-w-0">
      <h3 className="text-lg sm:text-xl font-medium text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.label}>
            <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">{r.label}</p>
            <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${(r.share / max) * 100}%` }} />
            </div>
            <p className="mt-1.5 text-right text-xs">
              <span className="font-medium text-gray-900 dark:text-gray-100">{r.share}%</span>
              <span className="ml-3 text-gray-500 dark:text-gray-400">{format(r.value)}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AutomateOverview: React.FC = () => {
  const [period, setPeriod] = useState<Period>(30);
  const [metric, setMetric] = useState<MetricId>("open");
  const data = useMemo(() => buildData(period), [period]);
  const active = METRICS.find((m) => m.id === metric)!;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6 space-y-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-900 dark:text-gray-100">Overview</h1>
        <div className="relative mt-4 w-full sm:w-48">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value) as Period)}
            className="w-full appearance-none h-11 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 pl-3.5 pr-10 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
        </div>

        <h2 className="mt-7 mb-3 text-lg sm:text-xl font-medium text-gray-900 dark:text-gray-100">Revenue</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <StatCard
            icon={<MonitorSmartphone size={18} className="text-gray-700 dark:text-gray-300" />}
            label="Total Revenue"
            value={money(data.revenueTotal)}
            delta={<Delta value={data.revenueDelta} suffix="%" />}
          />
          <StatCard
            icon={<Filter size={16} className="text-indigo-600 dark:text-indigo-400" />}
            label="Active workflows"
            value={String(data.activeWorkflows)}
            delta={<Delta value={data.workflowDelta} />}
          />
        </div>

        <div className="mt-6 h-64 sm:h-80 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="automateRevenueFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#E5E7EB" />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} minTickGap={24} dy={8} />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#9CA3AF" }} tickFormatter={(v) => money(v, true)} width={52} />
              <Tooltip
                formatter={(v) => [money(Number(v)), "Revenue"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
              />
              <Area type="linear" dataKey="revenue" stroke="#4338CA" strokeWidth={2.5} fill="url(#automateRevenueFill)" activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <BarList title="Revenue by channel" rows={data.revenueByChannel} format={(n) => money(n, true)} />
          <BarList title="Revenue by workflows" rows={data.revenueByWorkflow} format={(n) => money(n, true)} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-lg sm:text-xl font-medium text-gray-900 dark:text-gray-100">Conversions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 bg-indigo-50/50 dark:bg-gray-900/40 rounded-t-2xl overflow-hidden">
          {METRICS.map((m) => {
            const Icon = m.icon;
            const selected = m.id === metric;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMetric(m.id)}
                className={cn(
                  "text-left p-4 sm:p-5 border-l-2 transition-colors",
                  selected ? "bg-white dark:bg-gray-900 border-indigo-600" : "border-transparent hover:bg-white/60 dark:hover:bg-gray-900/60"
                )}
              >
                <p className="flex items-center gap-2 text-sm sm:text-base text-gray-900 dark:text-gray-100">
                  <Icon size={18} className="shrink-0 text-gray-700 dark:text-gray-300" />
                  {m.label}
                </p>
                <p className="mt-3 text-xl sm:text-2xl text-gray-900 dark:text-gray-100">{data.rates[m.id].toFixed(1)}%</p>
                <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">{m.description}</p>
              </button>
            );
          })}
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-b-2xl p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <BarList title={`${active.label} by channel`} rows={data.byChannel[metric]} format={(n) => `${n.toLocaleString()} ${active.countLabel}`} />
          <BarList title={`${active.label} by workflows`} rows={data.byWorkflow[metric]} format={(n) => `${n.toLocaleString()} ${active.countLabel}`} />
        </div>
      </div>
    </div>
  );
};

export default AutomateOverview;
